import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { ArrowRight } from "lucide-react";

/* Colored category tiles for the /loyalty overview page — each tile deep-links
   into /loyalty/catalog?cat=<key> so users land inside the pre-filtered list. */

const CAT_STYLE = {
  airline:     { grad: "from-sky-500 to-cyan-500",         glow: "shadow-sky-400/30" },
  hotel:       { grad: "from-fuchsia-500 to-pink-500",     glow: "shadow-fuchsia-400/30" },
  car:         { grad: "from-emerald-500 to-teal-500",     glow: "shadow-emerald-400/30" },
  cruise:      { grad: "from-blue-500 to-indigo-500",      glow: "shadow-blue-400/30" },
  rail:        { grad: "from-orange-500 to-amber-500",     glow: "shadow-orange-400/30" },
  card:        { grad: "from-violet-500 to-purple-500",    glow: "shadow-violet-400/30" },
  membership:  { grad: "from-amber-500 to-yellow-500",     glow: "shadow-amber-400/30" },
  supermarket: { grad: "from-lime-500 to-green-500",       glow: "shadow-lime-400/30" },
  department:  { grad: "from-rose-500 to-red-500",         glow: "shadow-rose-400/30" },
  shopping:    { grad: "from-pink-500 to-fuchsia-500",     glow: "shadow-pink-400/30" },
  other:       { grad: "from-slate-600 to-slate-800",      glow: "shadow-slate-500/30" },
};

export default function CategoryTiles() {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/loyalty-catalog/categories")
      .then((r) => setCats(r.data?.categories || []))
      .catch(() => setCats([]));
  }, []);

  if (!cats.length) return null;
  const total = cats.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="loyalty-category-tiles">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-600">Browse by category</div>
          <h2 className="font-heading font-900 text-3xl sm:text-4xl text-slate-900 tracking-tight mt-1">
            {total}+ rewards programs, sorted your way
          </h2>
        </div>
        <Link to="/loyalty/catalog" data-testid="loyalty-tiles-viewall"
          className="hidden sm:inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-700 text-sm">
          View all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3.5">
        {cats.map((c) => {
          const style = CAT_STYLE[c.key] || CAT_STYLE.other;
          return (
            <Link
              key={c.key}
              to={`/loyalty/catalog?cat=${c.key}`}
              data-testid={`loyalty-tile-${c.key}`}
              className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${style.grad} p-5 min-h-[132px] flex flex-col justify-between text-white shadow-lg hover:shadow-xl ${style.glow} transition-all hover:-translate-y-0.5`}
            >
              <span aria-hidden="true"
                className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/20 blur-xl" />
              <div className="relative text-3xl leading-none drop-shadow">{c.emoji}</div>
              <div className="relative">
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/85">
                  {c.count || 0} programs
                </div>
                <div className="font-serif font-800 text-lg leading-tight mt-0.5 drop-shadow">
                  {c.label}
                </div>
              </div>
              <ArrowRight className="absolute bottom-4 right-4 w-4 h-4 text-white/80 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
